import { trackEvent } from '@/lib/posthog';
import {
  clearCampaignAttribution,
  getCampaignAttributionAnalyticsProperties,
} from '@/features/campaign-quiz/campaignAttribution';
import type {
  DailyWeekendLeagueCtaAction,
  DailyWeekendLeagueCtaState,
} from '@/lib/experiments/dailyWeekendLeagueCta';

type AuthMethod = 'google' | 'facebook' | 'email' | 'phone';
type AuthMode = 'signin' | 'signup';

export type ExitToPlaySource =
  | 'results_main_menu'
  | 'party_results_main_menu'
  | 'match_quit'
  | 'matchmaking_exit'
  | 'ranked_blocked'
  | 'training_complete'
  | 'generic';

export interface ExitToPlayAnalyticsProps {
  source: ExitToPlaySource;
  matchId?: string | null;
  matchType?: string | null;
  mode?: string | null;
  variant?: string | null;
  resultVersion?: number | null;
  hadFinalResults?: boolean;
  finalResultsAckSent?: boolean;
  stage?: string | null;
}

export type RankedQueueIntentSource =
  | 'mode_select'
  | 'play_again'
  | 'retry'
  | 'recovery'
  | 'unknown';

export interface RankedQueueIntent {
  source: RankedQueueIntentSource;
  clientRequestId: string;
  createdAtMs: number;
}

interface PendingExitToPlayAnalytics extends ExitToPlayAnalyticsProps {
  startedAtMs: number;
  fromPath?: string | null;
}

const EXIT_TO_PLAY_PENDING_KEY = 'quizball.exit_to_play.pending';
const EXIT_TO_PLAY_PENDING_TTL_MS = 10 * 60 * 1000;
const RANKED_QUEUE_INTENT_KEY = 'quizball.ranked_queue.intent';
const RANKED_QUEUE_INTENT_TTL_MS = 10 * 60 * 1000;

function resultsExitProps(props: ExitToPlayAnalyticsProps) {
  return {
    source: props.source,
    match_id: props.matchId ?? null,
    match_type: props.matchType ?? null,
    mode: props.mode ?? null,
    variant: props.variant ?? null,
    result_version: props.resultVersion ?? null,
    had_final_results: Boolean(props.hadFinalResults),
    final_results_ack_sent: Boolean(props.finalResultsAckSent),
    stage: props.stage ?? null,
  };
}

function readPendingExitToPlay(): PendingExitToPlayAnalytics | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(EXIT_TO_PLAY_PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingExitToPlayAnalytics>;
    if (typeof parsed.startedAtMs !== 'number' || !Number.isFinite(parsed.startedAtMs)) {
      window.sessionStorage.removeItem(EXIT_TO_PLAY_PENDING_KEY);
      return null;
    }
    return parsed as PendingExitToPlayAnalytics;
  } catch {
    window.sessionStorage.removeItem(EXIT_TO_PLAY_PENDING_KEY);
    return null;
  }
}

function isRankedQueueIntentSource(value: unknown): value is RankedQueueIntentSource {
  return (
    value === 'mode_select' ||
    value === 'play_again' ||
    value === 'retry' ||
    value === 'recovery' ||
    value === 'unknown'
  );
}

function createClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createRankedQueueIntent(source: RankedQueueIntentSource = 'unknown'): RankedQueueIntent {
  return {
    source,
    clientRequestId: createClientRequestId(),
    createdAtMs: Date.now(),
  };
}

function readRankedQueueIntent(): RankedQueueIntent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(RANKED_QUEUE_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RankedQueueIntent>;
    if (
      !isRankedQueueIntentSource(parsed.source) ||
      typeof parsed.clientRequestId !== 'string' ||
      parsed.clientRequestId.trim().length === 0 ||
      typeof parsed.createdAtMs !== 'number' ||
      !Number.isFinite(parsed.createdAtMs)
    ) {
      window.sessionStorage.removeItem(RANKED_QUEUE_INTENT_KEY);
      return null;
    }
    return {
      source: parsed.source,
      clientRequestId: parsed.clientRequestId.trim(),
      createdAtMs: parsed.createdAtMs,
    };
  } catch {
    try {
      window.sessionStorage.removeItem(RANKED_QUEUE_INTENT_KEY);
    } catch {
      // Ignore storage cleanup failures.
    }
    return null;
  }
}

export function markRankedQueueIntent(source: RankedQueueIntentSource): RankedQueueIntent | null {
  if (typeof window === 'undefined') return null;
  const intent = createRankedQueueIntent(source);
  try {
    window.sessionStorage.setItem(RANKED_QUEUE_INTENT_KEY, JSON.stringify(intent));
  } catch {
    // Continue without persisted attribution; the queue join will fall back to unknown.
  }
  return intent;
}

export function consumeRankedQueueIntent(nowMs = Date.now()): RankedQueueIntent | null {
  if (typeof window === 'undefined') return null;
  const intent = readRankedQueueIntent();
  try {
    window.sessionStorage.removeItem(RANKED_QUEUE_INTENT_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
  if (!intent) return null;
  if (nowMs - intent.createdAtMs > RANKED_QUEUE_INTENT_TTL_MS) {
    return null;
  }
  return intent;
}

export function trackAuthStarted(
  method: AuthMethod = 'google',
  authMode: AuthMode = 'signin',
) {
  // Auth intent is not a completed signup: social buttons can authenticate
  // both new and returning users. Keep one canonical browser event and let the
  // backend's DB-insert-gated `account_created` event own real conversions.
  const campaign = getCampaignAttributionAnalyticsProperties();
  trackEvent('auth_started', { method, auth_mode: authMode, ...campaign });
}

export function trackAuthPanelShown(mode: string) {
  trackEvent('auth_panel_shown', {
    auth_mode: mode,
    surface: 'welcome',
    ...getCampaignAttributionAnalyticsProperties(),
  });
}

export function trackSignupPageView() {
  const campaign = getCampaignAttributionAnalyticsProperties();
  trackEvent('signup_page_view', {
    auth_mode: 'signup',
    ...campaign,
  });
}

export function trackLoginCompleted(method: AuthMethod = 'google') {
  trackEvent('login_completed', { method });
}

export function trackLogout() {
  trackEvent('logout');
}

export function trackOnboardingStarted() {
  trackEvent('onboarding_started');
}

export function trackOnboardingStepCompleted(step: string) {
  trackEvent('onboarding_step_completed', { step });
}

export function trackOnboardingCompleted() {
  const campaign = getCampaignAttributionAnalyticsProperties();
  trackEvent('onboarding_completed', Object.keys(campaign).length > 0 ? campaign : undefined);
}

type MobileVerificationStep = 'phone' | 'otp';
type MobileVerificationFailureReason =
  | 'invalid_phone'
  | 'invalid_otp'
  | 'number_in_use'
  | 'request_failed';

const MOBILE_VERIFICATION_SOURCE = 'onboarding' as const;
type MobileVerificationSource = 'onboarding' | 'play_home_reminder' | 'settings';

export function trackMobileVerificationPromptShown() {
  trackEvent('mobile_verification_prompt_shown', {
    source: MOBILE_VERIFICATION_SOURCE,
  });
}

export function trackMobileVerificationStarted(props: {
  attempt: number;
  timeToStartMs: number;
  source?: MobileVerificationSource;
}) {
  trackEvent('mobile_verification_started', {
    source: props.source ?? MOBILE_VERIFICATION_SOURCE,
    attempt: props.attempt,
    time_to_start_ms: props.timeToStartMs,
  });
}

export function trackMobileVerificationCompleted(props: {
  method: 'otp' | 'already_verified';
  sendAttempts: number;
  verifyAttempts: number;
  timeToCompleteMs: number;
  source?: MobileVerificationSource;
}) {
  trackEvent('mobile_verification_completed', {
    source: props.source ?? MOBILE_VERIFICATION_SOURCE,
    method: props.method,
    send_attempts: props.sendAttempts,
    verify_attempts: props.verifyAttempts,
    time_to_complete_ms: props.timeToCompleteMs,
  });
}

export function trackMobileVerificationSkipped(props: {
  step: MobileVerificationStep;
  timeOnPromptMs: number;
}) {
  trackEvent('mobile_verification_skipped', {
    source: MOBILE_VERIFICATION_SOURCE,
    step: props.step,
    time_on_prompt_ms: props.timeOnPromptMs,
  });
}

export function trackMobileVerificationFailed(props: {
  step: MobileVerificationStep;
  reason: MobileVerificationFailureReason;
  attempt: number;
  source?: MobileVerificationSource;
}) {
  trackEvent('mobile_verification_failed', {
    source: props.source ?? MOBILE_VERIFICATION_SOURCE,
    step: props.step,
    reason: props.reason,
    attempt: props.attempt,
  });
}

export function trackMobileVerificationReminderShown() {
  trackEvent('mobile_verification_reminder_shown', {
    source: 'play_home',
  });
}

export function trackMobileVerificationReminderClicked() {
  trackEvent('mobile_verification_reminder_clicked', {
    source: 'play_home',
    action: 'verify',
  });
}

export function trackMobileVerificationReminderDismissed(props: {
  snoozeDays: number;
}) {
  trackEvent('mobile_verification_reminder_dismissed', {
    source: 'play_home',
    snooze_days: props.snoozeDays,
  });
}

export function trackInAppBrowserBlocked(browser: string, isIOS: boolean, isAndroid: boolean) {
  trackEvent('in_app_browser_blocked', {
    in_app_browser: browser,
    platform: isIOS ? 'ios' : isAndroid ? 'android' : 'other',
  });
}

export function trackMatchmakingStarted(mode: string, variant?: string) {
  trackEvent('matchmaking_started', { mode, variant });
}

export function trackMatchmakingAiFallback(mode: string, wait_ms: number) {
  trackEvent('matchmaking_ai_fallback', { mode, wait_ms });
}

export function trackMatchmakingHumanFound(mode: string, wait_ms: number) {
  trackEvent('matchmaking_human_found', { mode, wait_ms });
}

export function trackMatchmakingCancelled(mode: string, wait_ms: number) {
  trackEvent('matchmaking_cancelled', { mode, wait_ms });
}

export function trackMatchStarted(props: {
  matchId: string;
  mode: string;
  variant?: string;
  opponentIsAi: boolean;
  opponentRp?: number;
}) {
  const campaign = getCampaignAttributionAnalyticsProperties();
  trackEvent('match_started', {
    match_id: props.matchId,
    mode: props.mode,
    variant: props.variant,
    opponent_is_ai: props.opponentIsAi,
    opponent_rp: props.opponentRp,
    ...campaign,
  });
  if (Object.keys(campaign).length > 0) clearCampaignAttribution();
}

export function trackMatchCompleted(props: {
  matchId: string;
  mode: string;
  variant?: string;
  won: boolean;
  score: number;
  opponentScore: number;
  durationSec?: number;
  questionsAnswered?: number;
  correctAnswers?: number;
  rpChange?: number;
  opponentIsAi?: boolean;
  goalsFor?: number;
  goalsAgainst?: number;
  penaltyGoalsFor?: number;
  penaltyGoalsAgainst?: number;
  winnerDecisionMethod?: string | null;
}) {
  const accuracy =
    props.questionsAnswered && props.correctAnswers !== undefined
      ? (props.correctAnswers / Math.max(1, props.questionsAnswered)) * 100
      : undefined;
  trackEvent('match_completed', {
    match_id: props.matchId,
    mode: props.mode,
    variant: props.variant,
    won: props.won,
    score: props.score,
    opponent_score: props.opponentScore,
    duration_sec: props.durationSec,
    questions_answered: props.questionsAnswered,
    correct_answers: props.correctAnswers,
    accuracy,
    rp_change: props.rpChange,
    opponent_is_ai: props.opponentIsAi,
    goals_for: props.goalsFor,
    goals_against: props.goalsAgainst,
    penalty_goals_for: props.penaltyGoalsFor,
    penalty_goals_against: props.penaltyGoalsAgainst,
    winner_decision_method: props.winnerDecisionMethod,
  });
}

export function trackMatchResultsViewed(
  matchType: 'ranked' | 'friendly',
  won: boolean,
  score: number,
  opponentScore: number,
  rpChange?: number,
) {
  trackEvent('match_results_viewed', {
    match_type: matchType,
    won,
    score,
    opponent_score: opponentScore,
    rp_change: rpChange,
  });
}

export function trackResultsMainMenuClicked(props: ExitToPlayAnalyticsProps): void {
  trackEvent('results_main_menu_clicked', resultsExitProps(props));
}

export function trackExitToPlayStarted(props: ExitToPlayAnalyticsProps): void {
  trackEvent('exit_to_play_started', {
    ...resultsExitProps(props),
    started_at_ms: Date.now(),
  });
}

export function markExitToPlayPending(props: ExitToPlayAnalyticsProps): void {
  if (typeof window === 'undefined') return;
  const pending: PendingExitToPlayAnalytics = {
    ...props,
    startedAtMs: Date.now(),
    fromPath: window.location.pathname,
  };
  window.sessionStorage.setItem(EXIT_TO_PLAY_PENDING_KEY, JSON.stringify(pending));
}

export function consumeExitToPlayPending(nowMs = Date.now()): PendingExitToPlayAnalytics | null {
  if (typeof window === 'undefined') return null;
  const pending = readPendingExitToPlay();
  if (!pending) return null;
  window.sessionStorage.removeItem(EXIT_TO_PLAY_PENDING_KEY);
  if (nowMs - pending.startedAtMs > EXIT_TO_PLAY_PENDING_TTL_MS) {
    return null;
  }
  return pending;
}

export function trackExitToPlayLanded(
  props: PendingExitToPlayAnalytics & { landedPath?: string | null; nowMs?: number },
): void {
  const nowMs = props.nowMs ?? Date.now();
  trackEvent('exit_to_play_landed', {
    ...resultsExitProps(props),
    started_at_ms: props.startedAtMs,
    elapsed_ms: Math.max(0, nowMs - props.startedAtMs),
    from_path: props.fromPath ?? null,
    landed_path: props.landedPath ?? null,
  });
}

export function trackRankedPlayAgainClicked(props: {
  matchId?: string | null;
  matchType?: string | null;
  mode?: string | null;
  stage?: string | null;
  cachedTickets?: number | null;
  walletFetching?: boolean;
  playAgainDisabled?: boolean;
  winStreakExperimentVariant?: string | null;
  winStreakCount?: number | null;
}): void {
  trackEvent('ranked_play_again_clicked', {
    match_id: props.matchId ?? null,
    match_type: props.matchType ?? null,
    mode: props.mode ?? null,
    stage: props.stage ?? null,
    cached_tickets: props.cachedTickets ?? null,
    wallet_fetching: Boolean(props.walletFetching),
    play_again_disabled: Boolean(props.playAgainDisabled),
    win_streak_experiment_variant: props.winStreakExperimentVariant ?? null,
    win_streak_count: props.winStreakCount ?? null,
  });
}

export function trackRankedWinStreakPromptShown(props: {
  matchId?: string | null;
  winStreakCount: number;
}): void {
  trackEvent('ranked_win_streak_prompt_shown', {
    match_id: props.matchId ?? null,
    win_streak_count: props.winStreakCount,
  });
}

export function trackMatchForfeit(matchId: string, reason: string, elapsedSec?: number) {
  trackEvent('match_forfeit', {
    match_id: matchId,
    reason,
    elapsed_sec: elapsedSec,
  });
}

export function trackMatchDisconnected(matchId: string, elapsedSec: number, phase?: string) {
  trackEvent('match_disconnected', {
    match_id: matchId,
    elapsed_sec: elapsedSec,
    phase,
  });
}

export function trackMatchReconnected(matchId: string, downtimeSec: number) {
  trackEvent('match_reconnected', {
    match_id: matchId,
    downtime_sec: downtimeSec,
  });
}

// NOTE: `answer_submitted` was removed — it fired ~12× per match and is fully
// redundant with the `match_answers` Postgres table (is_correct, time_ms,
// points_earned, selected_index, etc. per answer). Query that table with SQL.

export function trackQuestionSkipped(props: {
  matchId?: string;
  questionId: string;
  questionIndex: number;
  reason: 'time_out' | 'no_selection';
}) {
  trackEvent('question_skipped', {
    match_id: props.matchId,
    question_id: props.questionId,
    question_index: props.questionIndex,
    reason: props.reason,
  });
}

export function trackLifelineUsed(matchId: string | undefined, type: '5050' | 'clue' | 'skip') {
  trackEvent('lifeline_used', {
    match_id: matchId,
    type,
  });
}

export function trackDraftStarted(matchId: string) {
  trackEvent('draft_started', { match_id: matchId });
}

export function trackDraftCompleted(matchId: string, durationSec: number) {
  trackEvent('draft_completed', { match_id: matchId, duration_sec: durationSec });
}

export function trackPossessionPhaseEntered(matchId: string, phase: 'first_half' | 'second_half' | 'penalty') {
  trackEvent('possession_phase_entered', { match_id: matchId, phase });
}

export function trackPenaltyTaken(matchId: string, scored: boolean, attemptNumber: number) {
  trackEvent('penalty_taken', {
    match_id: matchId,
    scored,
    attempt_number: attemptNumber,
  });
}

export function trackModeSelected(mode: string) {
  trackEvent('mode_selected', { mode });
}

// ── Weekend League funnel ────────────────────────────────────────────────────
// banner viewed → banner clicked → tab viewed → entry → check-in. The DB holds
// the authoritative entry/check-in truth; these exist so the top of the funnel
// (who SAW and who CLICKED) is measurable at all.

/** The home-rail banner rendered for this visit (fires once per /play mount). */
export function trackWlBannerViewed() {
  trackEvent('wl_banner_viewed');
}

export function trackWlBannerClicked() {
  trackEvent('wl_banner_clicked');
}

/** The league tab opened; phase distinguishes entry-open browsing from
 *  live-event spectating ('unknown' if the visit ends before status loads). */
export function trackWlTabViewed(phase: string, hasEntered: boolean) {
  trackEvent('wl_tab_viewed', { phase, has_entered: hasEntered });
}

export function trackWlEntryCompleted() {
  trackEvent('wl_entry_completed');
}

export function trackWlCheckinCompleted(stage: 'qualifier' | 'final') {
  trackEvent('wl_checkin_completed', { stage });
}

/** Test A (ranked-results-wl-qp-toast): the QP toast rendered / was tapped. */
export function trackWlQpToastShown(gainedQp: number, newQpTotal: number) {
  trackEvent('wl_qp_toast_shown', { gained_qp: gainedQp, new_qp_total: newQpTotal });
}

export function trackWlQpToastClicked(newQpTotal: number) {
  trackEvent('wl_qp_toast_clicked', { new_qp_total: newQpTotal });
}

export function trackCategorySelected(categoryId: string, categoryName: string) {
  trackEvent('category_selected', {
    category_id: categoryId,
    category_name: categoryName,
  });
}

export function trackLobbyCreated(mode: string) {
  trackEvent('lobby_created', { mode });
}

export function trackLobbyJoined(lobbyId: string, inviteCode?: string) {
  trackEvent('lobby_joined', {
    lobby_id: lobbyId,
    via_invite_code: !!inviteCode,
  });
}

export function trackFriendInviteSent(method: 'link_copy' | 'code_copy' | 'social_share', lobbyId?: string) {
  trackEvent('friend_lobby_invite_sent', { method, lobby_id: lobbyId });
}

/** A refused invite's way forward was tapped (sign up, new room, try again). */
export function trackFriendInviteRecovery(props: { action: 'sign_up' | 'new_room' | 'try_again'; failureCode: string; roomState: string | null }) {
  trackEvent('friend_lobby_invite_recovery_click', { action: props.action, failure_code: props.failureCode, room_state: props.roomState });
}

export function trackFriendInviteAccepted(lobbyId: string) {
  trackEvent('friend_lobby_invite_accepted', { lobby_id: lobbyId });
}

export function trackFriendInviteLinkOpened() {
  trackEvent('friend_lobby_invite_link_opened', {
    source: 'shared_link',
    authenticated: true,
  });
}

export function trackFriendInviteJoinAttempted(props: {
  attemptNumber: number;
}) {
  trackEvent('friend_lobby_invite_join_attempted', {
    source: 'shared_link',
    attempt_number: props.attemptNumber,
  });
}

export function trackFriendInviteJoinFailed(props: {
  failureCode: string;
  retryable: boolean;
  correlationId?: string | null;
  attemptNumber: number;
  stateConfirmationTimedOut?: boolean;
  /** What the server said about the room (open / in_progress / ended / unknown), when it said. */
  roomState?: string | null;
  principalKind?: string;
}) {
  trackEvent('friend_lobby_invite_join_failed', {
    source: 'shared_link',
    failure_code: props.failureCode,
    retryable: props.retryable,
    correlation_id: props.correlationId ?? null,
    attempt_number: props.attemptNumber,
    state_confirmation_timed_out: Boolean(props.stateConfirmationTimedOut),
    room_state: props.roomState ?? null,
    principal_kind: props.principalKind ?? null,
  });
}

export function trackFriendInviteJoinSucceeded(props: {
  lobbyId: string;
  attemptNumber: number;
}) {
  trackEvent('friend_lobby_invite_join_succeeded', {
    source: 'shared_link',
    lobby_id: props.lobbyId,
    attempt_number: props.attemptNumber,
  });
}

export function trackPartyQuizStarted(playerCount: number) {
  trackEvent('party_quiz_started', { player_count: playerCount });
}

export function trackPartyQuizCompleted(props: {
  playerCount: number;
  finishPosition: number;
  totalQuestions: number;
  correctAnswers: number;
}) {
  trackEvent('party_quiz_completed', {
    player_count: props.playerCount,
    finish_position: props.finishPosition,
    total_questions: props.totalQuestions,
    correct_answers: props.correctAnswers,
  });
}

export function trackChallengeInviteSent(targetUserId: string) {
  trackEvent('challenge_invite_sent', { target_user_id: targetUserId });
}

export function trackChallengeInviteAccepted(invitationId: string) {
  trackEvent('challenge_invite_accepted', { invitation_id: invitationId });
}

export function trackChallengeInviteDeclined(invitationId: string) {
  trackEvent('challenge_invite_declined', { invitation_id: invitationId });
}

export function trackDailyChallengeStarted(challengeType: string) {
  trackEvent('daily_challenge_started', { challenge_type: challengeType });
}

export function trackDailyChallengeQuit(props: {
  challengeType: string;
  /** Optional — most games don't expose progress at the route boundary. */
  questionsAnswered?: number;
}) {
  trackEvent('daily_challenge_quit', {
    challenge_type: props.challengeType,
    questions_answered: props.questionsAnswered,
  });
}

export function trackDailyChallengeCompleted(props: {
  challengeType: string;
  score?: number;
  correctAnswers?: number;
  totalQuestions?: number;
  xpAwarded?: number;
  coinsAwarded?: number;
}) {
  trackEvent('daily_challenge_completed', {
    challenge_id: props.challengeType,
    challenge_type: props.challengeType,
    score: props.score,
    correct_answers: props.correctAnswers,
    total_questions: props.totalQuestions,
    xp_awarded: props.xpAwarded,
    coins_awarded: props.coinsAwarded,
  });
}

type DailyWeekendLeagueCtaAnalyticsProps = {
  state: DailyWeekendLeagueCtaState;
  action: DailyWeekendLeagueCtaAction;
  currentQp: number;
  targetQp: number;
  tournamentStatus: string | null;
};

function dailyWeekendLeagueCtaProperties(props: DailyWeekendLeagueCtaAnalyticsProps) {
  return {
    state: props.state,
    action: props.action,
    current_qp: props.currentQp,
    target_qp: props.targetQp,
    tournament_status: props.tournamentStatus,
  };
}

export function trackDailyWeekendLeagueCtaShown(
  props: DailyWeekendLeagueCtaAnalyticsProps,
) {
  trackEvent(
    'daily_weekend_league_cta_shown',
    dailyWeekendLeagueCtaProperties(props),
  );
}

export function trackDailyWeekendLeagueCtaClicked(
  props: DailyWeekendLeagueCtaAnalyticsProps,
) {
  trackEvent(
    'daily_weekend_league_cta_clicked',
    dailyWeekendLeagueCtaProperties(props),
  );
}

export function trackDailyComebackPromptShown(props: {
  streakDays: number;
  tomorrowBonusCoins: number;
  remindersEnabled: boolean;
}) {
  trackEvent('daily_comeback_prompt_shown', {
    streak_days: props.streakDays,
    tomorrow_bonus_coins: props.tomorrowBonusCoins,
    reminders_enabled: props.remindersEnabled,
  });
}

export function trackDailyComebackReminder(props: {
  outcome: 'clicked' | 'scheduled' | 'failed';
  streakDays: number;
}) {
  trackEvent(`daily_comeback_reminder_${props.outcome}`, {
    streak_days: props.streakDays,
    channel: 'email',
  });
}

export function trackRankedLossRecoveryPromptShown(props: {
  previousRp: number;
  currentRp: number;
  rpToRecover: number;
}) {
  trackEvent('ranked_loss_recovery_prompt_shown', {
    previous_rp: props.previousRp,
    current_rp: props.currentRp,
    rp_to_recover: props.rpToRecover,
  });
}

export function trackRankedLossRecoveryPlayAgainClicked(props: {
  rpToRecover: number;
}) {
  trackEvent('ranked_loss_recovery_play_again_clicked', {
    rp_to_recover: props.rpToRecover,
  });
}

export function trackWeekendLeagueProgressRail(
  outcome: 'shown' | 'clicked',
  props: {
    state: 'qualifying' | 'qualified' | 'entered';
    currentQp: number;
    targetQp: number;
    tournamentStatus: string;
  },
) {
  trackEvent(`weekend_league_progress_rail_${outcome}`, {
    state: props.state,
    current_qp: props.currentQp,
    target_qp: props.targetQp,
    tournament_status: props.tournamentStatus,
  });
}

export function trackStoreViewed() {
  trackEvent('store_viewed');
}

export function trackPurchaseModalOpened(
  productSlug: string,
  mode: 'coins' | 'stripe' | 'equip' | 'none',
  options?: { affordable?: boolean },
) {
  trackEvent('purchase_modal_opened', {
    product_slug: productSlug,
    mode,
    // Distinguishes real purchase intent from "Need more" previews so the
    // open -> confirm/cancel funnel isn't skewed by broke-user window shopping.
    affordable: options?.affordable ?? true,
  });
}

export function trackPurchaseCancelled(productSlug: string) {
  trackEvent('purchase_cancelled', { product_slug: productSlug });
}

export function trackCoinBalanceChanged(props: {
  delta: number;
  source: 'match_win' | 'daily' | 'purchase' | 'objective' | 'admin' | 'signup';
  newBalance: number;
}) {
  trackEvent('coin_balance_changed', {
    delta: props.delta,
    source: props.source,
    new_balance: props.newBalance,
  });
}

export function trackAvatarPartEquipped(slot: string, partId: string) {
  trackEvent('avatar_part_equipped', { slot, part_id: partId });
}

export function trackItemPurchased(itemId: string, itemName: string, cost: number, currency: 'coins' | 'usd' = 'coins') {
  trackEvent('item_purchased', {
    item_id: itemId,
    item_name: itemName,
    cost,
    currency,
  });
}

export function trackNicknameChanged() {
  trackEvent('nickname_changed');
}

export function trackFavoriteClubChanged(clubId: string) {
  trackEvent('favorite_club_changed', { club_id: clubId });
}

export function trackLanguageSwitched(from: string, to: string) {
  trackEvent('language_switched', { from, to });
}

export function trackSettingsOpened() {
  trackEvent('settings_opened');
}

export function trackLevelUp(newLevel: number) {
  trackEvent('level_up', {
    new_level: newLevel,
  });
}

export function trackDivisionPromoted(newDivision: string, newRankPoints: number) {
  trackEvent('division_promoted', {
    new_division: newDivision,
    new_rank_points: newRankPoints,
  });
}

export function trackAchievementUnlocked(achievementId: string, achievementName: string) {
  trackEvent('achievement_unlocked', {
    achievement_id: achievementId,
    achievement_name: achievementName,
  });
}

export function trackSocketConnectionFailed(error: string) {
  trackEvent('socket_connection_failed', { error });
}

export function trackSocketReconnected(downtimeSec: number) {
  trackEvent('socket_reconnected', { downtime_sec: downtimeSec });
}

export function trackApiError(
  endpoint: string,
  status: number,
  code?: string,
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete',
) {
  // A profile read and profile edit can share a route/status. Record the known
  // operation, but do not invent one for existing callers or include payloads.
  trackEvent('api_error', { endpoint, status, code, ...(method ? { method: method.toUpperCase() } : {}) });
}

export function trackMatchLoadError(matchId: string | undefined, errorCode: string) {
  trackEvent('match_load_error', { match_id: matchId, error_code: errorCode });
}

// Note: `trackGameStart`, `trackGameComplete`, `trackMatchJoined` previously
// lived here but were never called. They duplicated `trackMatchStarted` /
// `trackMatchCompleted` / lobby-join flow. Removed to keep the event surface
// minimal — if a future use case appears, prefer extending the canonical
// match_started / match_completed events instead of introducing new ones.

/** A game card on the hub was selected. */
export function trackPlayCardClicked(props: { slug: string; group: "daily" | "coins" | "other"; destination: "route" | "demo" | "modal" | "auth" | "public_page" }) {
  trackEvent("play_card_clicked", props);
}
